import type { ReactNode } from 'react';
import type { OmenViewKind } from '../../../engine';

/**
 * An Omen on the board (docs/LONG_NIGHT_ART_DIRECTION.md §12.9): a rune of light etched into the
 * board's dark glass under the pieces, thin lines with a shape of its own, warm for the Omens
 * that give and cold for those that take. A Shrouded square is only a shade darker than its
 * neighbors, with a slow turn of smoke inside it. Each rune etches itself in when it appears.
 */
export function OmenRune({
  kind,
  uses = 0,
  open = false,
}: {
  kind: OmenViewKind;
  /** Landings counted: a Candle shortens as it burns (out at 3). */
  uses?: number;
  /** Something stands on it (the Mimic's eye opens). */
  open?: boolean;
}) {
  if (kind === 'unknown') return <span className="rune rune-unknown" aria-hidden="true" />;
  return (
    <span className={`rune rune-${kind} ${open ? 'is-open' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 40 40">{RUNES[kind](uses)}</svg>
    </span>
  );
}

const RUNES: Record<Exclude<OmenViewKind, 'unknown'>, (uses: number) => ReactNode> = {
  // A flame, warm, that shortens as it burns out.
  candle: (uses) => {
    const h = 14 - Math.min(3, uses) * 3.5;
    return (
      <>
        <path className="r-line" d={`M17 34 V${30 - h} H23 V34`} />
        <path
          className="r-glow"
          d={`M20 ${22 - h} C22.5 ${25 - h} 23.5 ${27 - h} 23 ${29 - h} A3 3 0 0 1 17 ${29 - h} C16.6 ${27 - h} 17.8 ${25 - h} 20 ${22 - h}Z`}
        />
      </>
    );
  },
  // An inlaid square of brass.
  'gilt-tile': () => (
    <>
      <rect className="r-fill" x="9" y="9" width="22" height="22" rx="1.5" />
      <path className="r-line" d="M9 9 L31 31 M31 9 L9 31" opacity="0.35" />
      <rect className="r-line" x="12" y="12" width="16" height="16" rx="1" />
    </>
  ),
  // A ring of white dots.
  'salt-circle': () => (
    <>
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i / 12) * Math.PI * 2;
        return (
          <circle
            key={i}
            className="r-dot"
            cx={20 + Math.cos(a) * 12}
            cy={20 + Math.sin(a) * 12}
            r="1.3"
          />
        );
      })}
    </>
  ),
  // A block of stone: the only opaque thing on the board, standing proud of it.
  pillar: () => (
    <>
      <rect className="r-stone" x="9" y="8" width="22" height="24" rx="1.5" />
      <path className="r-stone-top" d="M9 12 H31" />
      <path className="r-stone-line" d="M14 16 V28 M20 16 V28 M26 16 V28" />
    </>
  ),
  // Rings of ripples.
  well: () => (
    <>
      <circle className="r-line r-ripple" cx="20" cy="20" r="4" />
      <circle className="r-line r-ripple is-2" cx="20" cy="20" r="9" />
      <circle className="r-line r-ripple is-3" cx="20" cy="20" r="14" />
    </>
  ),
  // A black pool with a sheen.
  'ink-pool': () => (
    <>
      <ellipse className="r-ink" cx="20" cy="21" rx="13" ry="9" />
      <path className="r-sheen" d="M12 18 C15 14.5 22 14 27 16.5" />
    </>
  ),
  // A chalked 3 in a ring: the number that scores on it (no crosses on the board).
  'chalk-x': () => (
    <>
      <circle className="r-chalk" cx="20" cy="20" r="13" />
      <path className="r-chalk" d="M15.5 12.5h9l-4.7 6.2a4.9 4.9 0 1 1-4.9 6.5" />
    </>
  ),
  // A thin bramble of iron.
  thorns: () => (
    <>
      <path className="r-iron" d="M6 30 C12 24 16 26 20 20 S28 14 34 10" />
      <path
        className="r-iron"
        d="M11 26.5 l-2.5 -3 M15.5 24.5 l1 -3.5 M20 20 l-3 -1.5 M24 16.6 l1.6 3 M28.5 13.5 l-1 -3.4 M31.5 11.4 l2.6 2.4"
      />
    </>
  ),
  // A small gold arch, on the back rows.
  altar: () => (
    <>
      <path className="r-line" d="M11 33 V20 A9 9 0 0 1 29 20 V33" />
      <path className="r-line" d="M8 33 H32" />
      <circle className="r-glow" cx="20" cy="22" r="2.2" />
    </>
  ),
  // A square of absolute black in a thin iron frame.
  trapdoor: () => (
    <>
      <rect className="r-void" x="8" y="8" width="24" height="24" />
      <rect className="r-iron" x="8" y="8" width="24" height="24" />
      <path className="r-iron" d="M8 20 H11 M29 20 H32" />
    </>
  ),
  // A ring of brass that fades once it is taken.
  'lost-ring': () => (
    <>
      <circle className="r-brass" cx="20" cy="22" r="8" />
      <path className="r-glow" d="M20 11.5 l2.4 2.6 -2.4 2.6 -2.4 -2.6z" />
    </>
  ),
  // Pale lines of wind, turning.
  draft: () => (
    <g className="r-turn">
      <path className="r-wind" d="M8 15 C14 11 20 19 28 13 C31 11 33 12 33 14" />
      <path className="r-wind" d="M6 21 C14 17 22 25 32 19" />
      <path className="r-wind" d="M9 27 C15 23 21 31 28 25" />
    </g>
  ),
  // An oval of silver glass with a slow swirl in it: the way through to the one opposite.
  'looking-glass': () => (
    <>
      <ellipse className="r-glass-frame" cx="20" cy="20" rx="10" ry="14" />
      <ellipse className="r-glass" cx="20" cy="20" rx="7.5" ry="11.5" />
      <g className="r-swirl">
        <path className="r-line" d="M20 12 C26 13 26 22 20 23 C16 23.5 15.5 18 19 17.5" />
        <path className="r-line" d="M20 28 C14 27 14 18 20 17" opacity="0.55" />
      </g>
    </>
  ),
  // A bowl of coals on three legs, flames over it that roar when a pawn stands in it.
  brazier: () => (
    <>
      <path className="r-iron" d="M14 34 L17 27 M26 34 L23 27 M20 34 V27" />
      <path className="r-brazier" d="M9 21 H31 C30 26 26 28 20 28 C14 28 10 26 9 21Z" />
      <g className="r-flames">
        <path
          className="r-glow"
          d="M20 6 C23 10 25.5 13 24.5 17 C24 19.5 22 21 20 21 C17.5 21 15.5 19 15.6 16.5 C15.8 13.6 18.6 11.6 20 6Z"
        />
        <path
          className="r-flame-in"
          d="M20 12 C21.6 14.4 22.6 16.2 22 18.2 C21.6 19.4 20.8 20 20 20 C18.8 20 18 19 18.1 17.8 C18.3 16 19.4 14.6 20 12Z"
        />
        <path
          className="r-glow is-side"
          d="M13 13 C14.6 15 15 17 14 19.5 C12.6 18 12.2 15.4 13 13Z"
        />
        <path
          className="r-glow is-side"
          d="M27 13 C25.4 15 25 17 26 19.5 C27.4 18 27.8 15.4 27 13Z"
        />
      </g>
    </>
  ),
  // An eye that opens when something lands on it.
  mimic: () => (
    <>
      <path className="r-line r-lid" d="M7 20 C13 12 27 12 33 20 C27 28 13 28 7 20Z" />
      <circle className="r-iris" cx="20" cy="20" r="4.5" />
      <path className="r-line r-shut" d="M7 20 C13 25 27 25 33 20" />
    </>
  ),
};
